import { Injectable } from '@angular/core';
import { QUALIFICATIONS_CATEGORIES } from '../../shared/constants/qualification-categories';
import {
  AdvanceTargetingQualification,
  QualificationForNest,
  Qualification,
} from '../../shared/interfaces/qualification.interface';
import {
  Criteria,
  NestedQuota,
  OrderNestedQuota,
  QuotaPayload,
} from '../../shared/interfaces/quota.interface';
import {
  LOCATION_QUALIFICATION_CODES,
  QUALIFICATION_CODES,
} from '../../shared/constants/qualification-code';
import { Constants } from '../../shared/constants/create-survey.constants';
import { CommonService } from '../../shared/services/common-service/common-service';
import { ChildrenLabelMapper } from 'purespectrum-lib';

@Injectable({ providedIn: 'root' })
export class QuotaNestingService {
  public constructor(private _commonService: CommonService) {}
  public nestQuotas(
    quotas: QuotaPayload[],
    qualifications: Qualification[] | QualificationForNest[],
    advancedTargetingQualifications:
      | AdvanceTargetingQualification[]
      | QualificationForNest[],
    completes: number
  ): NestedQuota[] | undefined {
    const selectedQualificationsToNest = this.getAllQualificationsForNest(
      qualifications,
      advancedTargetingQualifications
    );
    return this.nestQuotasForQualifications(
      quotas,
      selectedQualificationsToNest,
      completes
    );
  }

  public nestQuotasForQualifications(
    quotas: QuotaPayload[],
    qualificationsForNest: QualificationForNest[],
    completes: number,
    orderNestedQuotas?: OrderNestedQuota[]
  ) {
    if (!qualificationsForNest.length) {
      return;
    }

    const quotasToBeNested = this._getQuotasOfNestedQualifications(
      quotas,
      qualificationsForNest
    );
    const allQuotasNested = this._cartesianProduct(quotasToBeNested);
    return this._getNestedQuotas(allQuotasNested, completes, orderNestedQuotas);
  }

  /**
   * TODO
   * Logic that should be moved to backend
   */
  private _getNestedQuotas(
    quotasInNestedOrder: NestedQuota[][],
    surveyGoal: number,
    orderNestedQuotas?: OrderNestedQuota[]
  ) {
    const nestedQuotas = [];
    for (const quotas of quotasInNestedOrder) {
      if (orderNestedQuotas) {
        this._sortQuotasInNestedOrder(orderNestedQuotas, quotas);
      }
      const percentages: Array<number> = [];
      const combinedKey: Array<string> = [];
      const criterias = [];
      for (const quota of quotas) {
        percentages.push(Number(quota.percentage) / 100);
        combinedKey.push(quota.text || '');
        criterias.push(...(quota.criteria || ''));
      }
      const nestingPercentages = parseFloat(
        (
          percentages.reduce((memo, num) => {
            return memo * num;
          }, 1) * 100
        ).toFixed(2)
      );
      const nestingAllocation = Math.round(
        (surveyGoal * Number(nestingPercentages)) / 100
      );
      nestedQuotas.push(<NestedQuota>{
        buyer_quota_id: this._commonService.getRandomString(),
        number: nestingAllocation,
        percentage: nestingPercentages,
        minimum: nestingAllocation,
        maximum: nestingAllocation,
        combinedKey,
        flexiblePer: 0,
        fielded: 0,
        criteria: criterias,
        quota_category: QUALIFICATIONS_CATEGORIES.NESTED,
      });
    }

    this._correctNestedUiAllocation(nestedQuotas, surveyGoal);
    return nestedQuotas;
  }

  _sortQuotasInNestedOrder(
    orderNestedQuotas: OrderNestedQuota[],
    quotas: NestedQuota[]
  ) {
    const findQuotaOrder = (quota: NestedQuota): number =>
      orderNestedQuotas.find(
        (quotaOrder) =>
          quotaOrder.qualificationCode === quota.criteria[0].qualification_code
      )?.order || 0;
    quotas.sort((a, b) => findQuotaOrder(a) - findQuotaOrder(b));
  }

  /**
   * Legacy Code
   */
  private _cartesianProduct(arr: any[]) {
    return arr.reduce(
      function (a, b) {
        return (
          a
            .map(function (x: any[]) {
              return b.map(function (y: any) {
                return x.concat([y]);
              });
            })
            // eslint-disable-next-line @typescript-eslint/no-shadow
            .reduce(function (acc: string | any[], val: any) {
              return acc.concat(val);
            }, [])
        );
      },
      [[]]
    );
  }

  /**
   * Legacy code
   */
  // eslint-disable-next-line complexity
  private _correctNestedUiAllocation(
    nestedQuotas: NestedQuota[],
    surveyGoal: number
  ): NestedQuota[] {
    let totalAllocation: number = 0;

    for (const item of nestedQuotas) {
      if (item.number) {
        totalAllocation += item.number;
      }
    }

    if (totalAllocation != surveyGoal) {
      let diff = surveyGoal - totalAllocation;
      if (diff > 0) {
        for (const nestedQuota of nestedQuotas) {
          if (diff > 0) {
            nestedQuota.number = Number(nestedQuota.number) + 1;
            nestedQuota.minimum = nestedQuota.number;
            nestedQuota.maximum = nestedQuota.number;
            diff--;
          }
        }
      } else if (diff < 0) {
        diff = -diff;
        if (diff > 0) {
          for (const nestedQuota of nestedQuotas) {
            if (diff > 0) {
              nestedQuota.number =
                Number(nestedQuota.number) - 1 < 0
                  ? 0
                  : Number(nestedQuota.number) - 1;
              nestedQuota.minimum = nestedQuota.number;
              nestedQuota.maximum = nestedQuota.number;
              diff--;
            }
          }
        }
      }
    }

    return nestedQuotas;
  }

  public getAllQualificationsForNest(
    qualifications: Qualification[] | QualificationForNest[],
    advancedTargetingQualifications: AdvanceTargetingQualification[]
  ): QualificationForNest[] {
    return JSON.parse(
      JSON.stringify(
        (qualifications as any[]).concat(advancedTargetingQualifications)
      )
    ).filter((qualification: QualificationForNest) => qualification);
  }

  private _getQuotasOfNestedQualifications(
    quotas: QuotaPayload[],
    qualificationsToNest: Array<QualificationForNest>
  ): Array<Array<NestedQuota>> {
    const quotasToBeNested: Array<Array<NestedQuota>> = [];
    for (const qualification of qualificationsToNest) {
      qualification.hasNestedQuotas = true;
      const qualificationCodes = [qualification.qualification_code];
      const isChildren =
        qualification.qualification_code === QUALIFICATION_CODES.CHILDREN;
      if (isChildren) {
        qualificationCodes.push(
          QUALIFICATION_CODES.CHILD_GENDER,
          QUALIFICATION_CODES.CHILD_AGE
        );
      }
      let filteredQuotas: Array<QuotaPayload> = quotas.filter(
        (quota) =>
          this._getQuotaQualificationCode(quota).some((code) =>
            qualificationCodes.includes(code)
          ) && !qualification.isAdvanceTargetingParent
      );
      if (isChildren) {
        filteredQuotas = this._mapChildrenQuotas(filteredQuotas);
      }
      const selectedQuotas = filteredQuotas.map((quota) => {
        return <NestedQuota>{
          text: this._getQuotaDescription(qualification, quota),
          required_count: quota.required_count,
          percentage: quota.percentage || 0,
          minimum: quota.minimum || 0,
          maximum: quota.maximum || 0,
          criteria: quota.criteria,
        };
      });
      quotasToBeNested.push(selectedQuotas);
    }
    return quotasToBeNested;
  }

  private _mapChildrenQuotas(quotas: QuotaPayload[]) {
    const children = quotas.filter((q) =>
      this._getQuotaQualificationCode(q).includes(QUALIFICATION_CODES.CHILDREN)
    )!;
    if (!children?.length) {
      return quotas;
    }
    const haveChildren = children.find(
      (c) =>
        c.criteria[0].condition_codes![0] ===
        Constants.CHILDREN.HAVE_CHILDREN.toString()
    );
    if (!haveChildren || haveChildren.criteria.length > 1) {
      return quotas;
    }
    // remove have children to left only no children here
    children.splice(children.indexOf(haveChildren), 1);
    const gender = quotas.filter((q) =>
      this._getQuotaQualificationCode(q).includes(
        QUALIFICATION_CODES.CHILD_GENDER
      )
    );
    const age = quotas.filter((q) =>
      this._getQuotaQualificationCode(q).includes(QUALIFICATION_CODES.CHILD_AGE)
    );
    age.reverse();
    const childrenQuotas = age.map((a) => {
      let g = gender.shift();
      if (!g) {
        g = {
          ...a,
          criteria: [
            {
              ...a.criteria[0],
              condition_codes: [
                Constants.CHILD_GENDER.BOY,
                Constants.CHILD_GENDER.GIRL,
              ],
              condition_names: [
                // @ts-ignore
                Constants.CHILD_GENDER_MAPPING[Constants.CHILD_GENDER.BOY],
                // @ts-ignore
                Constants.CHILD_GENDER_MAPPING[Constants.CHILD_GENDER.GIRL],
              ],
              qualification_code: QUALIFICATION_CODES.CHILD_GENDER,
              qualification_name: 'Child Gender',
            },
          ],
        } as QuotaPayload;
        delete g.criteria[0].range_sets;
      }
      delete a.quota_title;
      return {
        ...a,
        criteria: [
          ...haveChildren.criteria.map((c) => ({ ...c })),
          ...a.criteria,
          ...g.criteria,
        ],
        quota_category: 'layered',
      } as QuotaPayload;
    });
    // add no children
    if (children.length) {
      childrenQuotas.push(...children);
    }
    return childrenQuotas;
  }

  private _getQuotaQualificationCode(quota: QuotaPayload): Array<number> {
    const qualificationsInQuotaCriteria: number[] = [];
    quota.criteria.forEach((criteria) => {
      qualificationsInQuotaCriteria.push(criteria.qualification_code);
    });
    return qualificationsInQuotaCriteria;
  }

  public getQuotaName(qualification: QualificationForNest, criteria: Criteria) {
    if (criteria?.range_sets) {
      const combined: string[] = [];
      criteria.range_sets.forEach((rangeSet: any) => {
        combined.push(`${rangeSet.from}-${rangeSet.to}`);
      });
      return combined.join(',');
    } else {
      if (
        qualification.db_qualification_code === QUALIFICATION_CODES.CHILDREN
      ) {
        return this._getQualificationTextForChildren(criteria, qualification);
      }
      return this._getQualificationText(qualification, criteria);
    }
  }

  private _getQuotaDescription(
    qualification: QualificationForNest,
    quota: QuotaPayload
  ): string {
    let description: Array<string> = [];
    quota.criteria.forEach((criteria: Criteria) => {
      if (criteria?.range_sets) {
        criteria.range_sets.forEach((rangeSet) => {
          description.push(`${rangeSet.from}-${rangeSet.to}`);
        });
      } else {
        if (
          qualification.db_qualification_code === QUALIFICATION_CODES.CHILDREN
        ) {
          description =
            this._getQualificationTextForChildren(criteria, qualification) !==
            ''
              ? description.concat(
                  this._getQualificationTextForChildren(criteria, qualification)
                )
              : description;
        } else {
          const text = this._getQualificationText(qualification, criteria);
          description = text !== '' ? description.concat(text) : description;
        }
      }
    });

    return description.join(',');
  }

  private _getQualificationTextForChildren(
    criteria: Criteria,
    qualification: QualificationForNest
  ) {
    if (criteria.qualification_code === QUALIFICATION_CODES.CHILDREN) {
      if (
        criteria.condition_codes?.length === 1 &&
        criteria.condition_codes[0] ===
          Constants.CHILDREN_CONDITION_CODES.NO_CHILDREN
      ) {
        const [option] = qualification.data
          ? qualification.data.filter(
              (condition) =>
                condition.id ===
                Number(Constants.CHILDREN_CONDITION_CODES.NO_CHILDREN)
            )
          : qualification.selectedAnswers.filter(
              (condition) => condition.id === 111
            );
        return option?.text;
      } else {
        return '';
      }
    }
    if (criteria.qualification_code === QUALIFICATION_CODES.CHILD_GENDER) {
      return criteria.condition_codes?.length === 1
        ? this._mapChildGender(criteria.condition_codes[0])
        : Constants.CHILD_GENDER_MAPPING[
            Constants.CHILD_GENDER.BOTH as keyof Object
          ];
    }
  }

  private _mapChildGender(conditionCode: string) {
    return conditionCode === Constants.CHILD_GENDER.BOY
      ? Constants.CHILD_GENDER_MAPPING[
          Constants.CHILD_GENDER.BOY as keyof Object
        ]
      : Constants.CHILD_GENDER_MAPPING[
          Constants.CHILD_GENDER.GIRL as keyof Object
        ];
  }

  private _getQualificationText(
    qualification: QualificationForNest,
    criteria: Criteria
  ) {
    const conditionCodes = this._getQuotaConditionCode(criteria);

    if (
      qualification.qualification_code === QUALIFICATION_CODES.CHILD_AGE_GENDER
    ) {
      const mapper = new ChildrenLabelMapper(conditionCodes);
      return mapper.label();
    }

    const texts = conditionCodes
      .map((code) => {
        if (
          LOCATION_QUALIFICATION_CODES.includes(
            qualification.qualification_code
          )
        ) {
          const conditionCode = qualification.data?.filter(
            (codes) => parseInt(codes.id) === code
          );
          if (conditionCode) {
            return criteria.group_name;
          }
        } else if (
          qualification.qualification_code === criteria.qualification_code &&
          !qualification.selectedAnswers &&
          !qualification.data.length
        ) {
          return criteria.condition_names;
        } else {
          const [option] = qualification.data
            ? qualification.data.filter((condition) => condition.id === code)
            : qualification.selectedAnswers.filter(
                (condition) => condition.id === code
              );
          return option?.text || option?.name;
        }
      })
      .filter((text) => !!text);
    return texts.length > 0 ? texts.join('--') : '';
  }

  private _getQuotaConditionCode(criteria: Criteria): number[] {
    const codes: number[] = [];

    (criteria.condition_codes ?? []).forEach((code) => {
      if (code) {
        codes.push(parseInt(code));
      }
    });
    return codes;
  }
}
